import { Transition, Box, MantineTransition } from '@mantine/core';
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

export interface TransitionStyles {
  in: React.CSSProperties;
  out: React.CSSProperties;
  common?: React.CSSProperties;
  transitionProperty?: string;
}

export interface CustomTransitionProps {
  children: React.ReactNode;
  visible?: boolean;
  styles: TransitionStyles;
  duration?: number;
  timingFunction?: string;
  delay?: number;
  onEnter?: () => void;
  onEntered?: () => void;
  onExit?: () => void;
  onExited?: () => void;
}

/**
 * Create custom transitions with full control over styles
 */
const CustomTransition = forwardRef<HTMLDivElement, CustomTransitionProps>(
  (
    {
      children,
      visible = true,
      styles,
      duration = 200,
      timingFunction = 'ease',
      delay = 0,
      onEnter,
      onEntered,
      onExit,
      onExited,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const actualDuration = reducedMotion ? 0 : duration;
    const transitionDef: MantineTransition = {
      ...styles,
      transitionProperty: styles.transitionProperty ?? 'all',
    };

    return (
      <Transition
        mounted={visible}
        transition={transitionDef}
        duration={actualDuration}
        timingFunction={timingFunction}
        onEnter={onEnter}
        onEntered={onEntered}
        onExit={onExit}
        onExited={onExited}
      >
        {(transitionStyles) => (
          <Box
            ref={ref}
            style={{
              ...transitionStyles,
              transitionDelay: reducedMotion ? '0ms' : `${delay}ms`,
            }}
          >
            {children}
          </Box>
        )}
      </Transition>
    );
  }
);

CustomTransition.displayName = 'CustomTransition';

export default CustomTransition;
